import isNil from 'lodash/isNil';
import PropTypes from 'prop-types';
import React, {
  CSSProperties,
  ReactNode,
  useEffect,
  useRef,
  useState
} from 'react';
import { twMerge } from 'tailwind-merge';

import Tokens from 'styles/tokens';

import * as HoverCard from '@radix-ui/react-hover-card';

interface Props {
  children?: ReactNode;
  triggerComponent: ReactNode;
  childrenStyles?: CSSProperties;
  contentStyles?: CSSProperties;
  contentProps?: object;
  title?: ReactNode;
  titleClasses?: string;
  titleActions?: ReactNode;
  openDelay?: number;
  closeDelay?: number;
  portalContainer?: HTMLElement;
  contentClasses?: string;
  isDisabled?: boolean;
  closeOnScroll?: boolean;
  childrenClasses?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** When true, shows a full-screen gray overlay behind the popover when open. */
  showOverlay?: boolean;
  /** When true, shows an arrow on the hover card. */
  showArrow?: boolean;
  /** The offset of the hover card. */
  offset?: number;
}

const AvomaHoverCard = ({
  triggerComponent,
  children,
  childrenStyles,
  contentStyles,
  contentProps,
  title = null,
  titleActions = null,
  openDelay = 100,
  closeDelay = 100,
  contentClasses = '',
  childrenClasses = '',
  titleClasses = '',
  portalContainer = document.body, // Use when the hover card needs to be rendered inside another portal like a Modal,
  closeOnScroll = false, // Close the hover card when the user scrolls outside the hover card
  isDisabled = false,
  open, // open and onOpenChange for when the hover card needs to be controlled
  onOpenChange,
  showOverlay = false,
  showArrow = false,
  offset
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const isControlled = !isNil(open);
  const usesCustomPortalContainer = portalContainer !== document.body;

  const handleOpen = (newOpen: boolean) => {
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
    setIsOpen(newOpen);
  };

  useEffect(() => {
    if (isControlled) {
      setIsOpen(Boolean(open));
    }
  }, [isControlled, open]);

  useEffect(() => {
    if (!closeOnScroll) return;

    // Detect scroll outside portal container and close hover card
    const handleScroll = (e: Event) => {
      if (
        contentRef.current &&
        !contentRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('scroll', handleScroll, true);

    return () => {
      document.removeEventListener('scroll', handleScroll, true);
    };
  }, [closeOnScroll]);

  const hoverCardRootProps = isControlled
    ? { open, onOpenChange: handleOpen }
    : { open: isOpen, onOpenChange: handleOpen };

  return (
    <HoverCard.Root
      openDelay={openDelay}
      closeDelay={closeDelay}
      {...hoverCardRootProps}
    >
      <HoverCard.Trigger asChild>{triggerComponent}</HoverCard.Trigger>
      {!isDisabled && (
        <HoverCard.Portal container={portalContainer}>
          <React.Fragment>
            {isOpen && showOverlay && (
              <div
                role='presentation'
                aria-hidden
                className='fixed inset-0 bg-black/10 z-[9998] pointer-events-none'
              />
            )}
            <HoverCard.Content
              sideOffset={offset ?? 4}
              style={{ ...styles.content, ...contentStyles }}
              className={twMerge('z-[1100]', contentClasses)}
              collisionPadding={{ top: 25, right: 25, bottom: 25, left: 25 }}
              {...(usesCustomPortalContainer
                ? { collisionBoundary: portalContainer }
                : {})}
              {...contentProps}
              ref={contentRef}
            >
              {(title || titleActions) && (
                <div className='sticky top-0 border-b border-b-gainsboro rounded-tl rounded-tr bg-snow py-2 px-4 flex items-center justify-between gap-2'>
                  {title && (
                    <p
                      className={twMerge(
                        'font-bold text-black text-base',
                        titleClasses
                      )}
                    >
                      {title}
                    </p>
                  )}
                  {titleActions}
                </div>
              )}
              <div
                style={{ ...styles.children, ...childrenStyles }}
                className={childrenClasses}
              >
                {children}
              </div>
              {showArrow && (
                <HoverCard.Arrow
                  className='fill-white  origin-center scale-125'
                  width={15}
                  height={10}
                />
              )}
            </HoverCard.Content>
          </React.Fragment>
        </HoverCard.Portal>
      )}
    </HoverCard.Root>
  );
};

const styles = {
  content: {
    ...Tokens.shadows.medium,
    width: 300,
    borderRadius: Tokens.spacing.borderRadius,
    border: Tokens.borders.standard
  },
  children: {
    background: Tokens.colors.white,
    padding: `${Tokens.spacing.one} ${Tokens.spacing.two}`,
    borderRadius: Tokens.spacing.borderRadius
  }
};

AvomaHoverCard.propTypes = {
  children: PropTypes.node,
  triggerComponent: PropTypes.node,
  childrenStyles: PropTypes.object,
  contentStyles: PropTypes.object,
  contentProps: PropTypes.object,
  title: PropTypes.node,
  openDelay: PropTypes.number,
  closeDelay: PropTypes.number,
  portalContainer: PropTypes.instanceOf(Element),
  contentClasses: PropTypes.string,
  childrenClasses: PropTypes.string,
  open: PropTypes.bool,
  onOpenChange: PropTypes.func,
  showOverlay: PropTypes.bool,
  showArrow: PropTypes.bool,
  offset: PropTypes.number
};

export default AvomaHoverCard;
